import React, {Fragment} from 'react';
import {View, ViewStyle} from 'react-native';
import {isNil} from 'lodash';

import Card from '/component/base/Card';
import Delta from '/component/base/Delta';
import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import {Cards} from '/constant/TestIDs';
import {COACH} from '/enum/competency.enum';
import {useTranslation} from '/provider/TranslationProvider';

import {styles} from './ClientsSummaryCard.style';
import {Competency, getClientSummaryKeys} from './ClientSummaryCard.util';

const MAX_FONT_SIZE_MULTIPLIER = 2;
interface Metrics {
  newCount?: number;
  tGenReactivatedCount?: number;
  frontlineReactivatedCount?: number;
  totalCount?: number;
  delta?: number;
  lastMonthCount?: number;
}
interface ClientsSummaryCardProps {
  metrics: Metrics;
  currentUserCompetency: Competency;
  style: ViewStyle;
}

export const ClientsSummaryCard: React.FC<ClientsSummaryCardProps> = ({
  currentUserCompetency,
  metrics = {},
  style = {},
}) => {
  const {
    newKey,
    newLabel,
    totalKey,
    totalLabel,
    newTestID,
    newTitleTestID,
    totalTestID,
    totalTitleTestID,
    totalGenReactivatedKey,
    totalGenReactivatedLabel,
    frontlineReactivatedKey,
    frontlineReactivatedLabel,
  } = getClientSummaryKeys(currentUserCompetency);

  const {tt} = useTranslation('screen.clients.card.clients');
  return (
    <Card
      fullWidth
      testID={Cards.clientsSummaryCard}
      style={{...styles.base, ...style}}
      accessible={false}>
      <View
        accessible={true}
        accessibilityRole="header"
        accessibilityLabel={'Clients Summary'}>
        <Text
          testID={Cards.clientsSummaryCardTitle}
          variant="heading"
          style={styles.cardHeaderText}
          allowFontScaling
          maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
          accessible={false}
          importantForAccessibility="no">
          {tt('header', 'Clients Summary')}
        </Text>
      </View>
      <ListItem
        accessibilityLabel={`New Clients ${metrics.newCount ?? 0}`}
        label={
          <Text
            testID={newTitleTestID}
            focusable={true}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt(`${newKey}`, `${newLabel}`)}
          </Text>
        }
        readonly
        suffix={
          <InlineFormattedNumber testID={newTestID} value={metrics.newCount} />
        }
        style={{base: styles.listItem, suffix: styles.listItemSuffix}}
      />
      {!isNil(metrics.tGenReactivatedCount) &&
        currentUserCompetency !== COACH && (
          <ListItem
            accessibilityLabel={`Total Gen Reactivated ${
              metrics.tGenReactivatedCount ?? 0
            }`}
            label={
              <Text
                testID={Cards.cardReactivatedTGenTitle}
                accessible={true}
                variant="body"
                emphasis="low"
                // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
                style={styles.listItemLabel}
                allowFontScaling
                maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
                {tt(`${totalGenReactivatedKey}`, `${totalGenReactivatedLabel}`)}
              </Text>
            }
            readonly
            suffix={
              <InlineFormattedNumber
                value={metrics.tGenReactivatedCount}
                testID={Cards.cardReactivatedTGenValue}
              />
            }
            style={{base: styles.listItem, suffix: styles.listItemSuffix}}
          />
        )}
      {!isNil(metrics.frontlineReactivatedCount) && (
        <ListItem
          accessibilityLabel={`Frontline Reactivated ${
            metrics.frontlineReactivatedCount ?? 0
          }`}
          label={
            <Text
              testID={Cards.cardReactivatedFrontlineTitle}
              accessible={true}
              variant="body"
              emphasis="low"
              // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
              style={styles.listItemLabel}
              allowFontScaling
              maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
              {tt(`${frontlineReactivatedKey}`, `${frontlineReactivatedLabel}`)}
            </Text>
          }
          readonly
          suffix={
            <InlineFormattedNumber
              value={metrics.frontlineReactivatedCount}
              testID={Cards.cardReactivatedFrontlineValue}
            />
          }
          style={{base: styles.listItem, suffix: styles.listItemSuffix}}
        />
      )}
      <ListItem
        accessibilityLabel={`Total Clients ${metrics.totalCount ?? 0}`}
        label={
          <Text
            testID={totalTitleTestID}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt(`${totalKey}`, `${totalLabel}`)}
          </Text>
        }
        readonly
        suffix={
          <Fragment>
            {!isNil(metrics.delta) ? (
              <Delta
                style={{base: styles.delta}}
                testID={Cards.cardAllClientsDeltaValue}
                value={metrics.delta}
              />
            ) : null}
            <InlineFormattedNumber
              testID={totalTestID}
              value={metrics.totalCount}
            />
          </Fragment>
        }
        style={{
          base: styles.listItem,
          suffix: {...styles.listItemSuffix, ...styles.value},
        }}
      />
      <View style={styles.divider} />
      <ListItem
        accessibilityLabel={`Last Month Total ${metrics.lastMonthCount ?? 0}`}
        label={
          <Text
            testID={Cards.cardLastMonthTotalTitle}
            accessible={true}
            variant="body"
            emphasis="low"
            // @ts-expect-error TS(2551): Property 'listItemLabel' does not exist on type '{... Remove this comment to see the full error message
            style={styles.listItemLabel}
            allowFontScaling
            maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}>
            {tt('lastMonth', 'Last Month Total')}
          </Text>
        }
        readonly
        suffix={
          <InlineFormattedNumber
            testID={Cards.cardLastMonthTotalValue}
            value={metrics.lastMonthCount}
          />
        }
        style={{
          base: {...styles.listItem, ...styles.listItemLast},
          suffix: styles.listItemSuffix,
        }}
      />
    </Card>
  );
};

export default ClientsSummaryCard;
